/**
 * Jobs data slice: the interface this feature's screens use + the live implementation over the US API
 * (core/http.ts → /api/v1/app/*). The fixture implementation (in-memory Lone Star tenant) is in ./fixture.ts, which is
 * loaded only in dev / VITE_FIXTURES builds. Shared entity reads (jobs, techs, customers, vehicles, catalog) live in
 * core/entities.ts; this slice holds the job workflows (create, actions, estimate loop, notes) and job-side reads.
 *
 * Paths: P1 jobs/estimates (`/v1/app/jobs…`), P2 saved views (`/v1/app/saved_views`), P4 calls/conversations,
 * P5 geo autocomplete, P6 offline payment (`/v1/app/pay/jobs/:id/cash`). Mutations carry one Idempotency-Key per user
 * intent and `If-Match: <version>` where the job has one; a stale version comes back as 409 `stale_version`.
 */
import { API_BASE, APP, ApiError, type Http } from '@app/core/http';
import type { Call, Cents, ISO, Job, JobEvent, Lead, Message, UUID } from '@app/core/types';
import { apiCall, apiLead, apiTimeline, currentEstimate, fetchJob, isMissingRoute, notAvailable } from '@app/core/live/dto';

export interface SavedView { id: UUID; module: string; name: string; query: Record<string, string>; is_shared: boolean; user_id: UUID | null; updated_at: ISO }
export interface GeoSuggestion { label: string; line1: string; city: string; state: string; zip: string | null; lat: number | null; lng: number | null }
export interface AddressInput { address_id?: UUID | null; line1: string; line2?: string | null; city: string; state: string; zip: string | null; lat?: number | null; lng?: number | null }
export interface LineInput { catalog_item_id: UUID | null; kind: 'labor' | 'part' | 'fee' | 'package'; description: string; qty: number; unit_price_cents: Cents; taxable: boolean }

export interface NewJobInput {
  lead_id: UUID | null;
  customer_id: UUID | null;
  customer: { first_name: string; last_name: string; company: string | null; phone_e164: string; email: string | null; sms_consent: 'granted' | 'unknown' } | null;
  vehicle_id: UUID | null;
  vehicle: { year: number | null; make: string; model: string; vin: string | null } | null;
  lines: LineInput[];
  address: AddressInput | null;
  location_id: UUID | null;
  scheduled_start: ISO | null;
  scheduled_end: ISO | null;
  customer_notes: string | null;
}
export interface JobPatch { internal_notes?: string | null; address?: AddressInput }
/** Body of POST /v1/app/jobs/:id/actions/:action (shape per action; the server validates). */
export interface ActionBody { reason?: string; note?: string; status?: string; start?: ISO; end?: ISO }
/** Extra fields on a 409 `stale_version`. */
export interface StaleInfo { current_version?: number; changed_by?: string | null; changed_at?: ISO | null }
export interface Mut { version?: number; key: string }

export interface Api {
  events(jobId: UUID): Promise<JobEvent[]>;
  calls(jobId: UUID): Promise<Call[]>;
  messages(customerId: UUID): Promise<Message[]>;
  lead(id: UUID): Promise<Lead>;
  geoAutocomplete(q: string): Promise<GeoSuggestion[]>;
  recordingUrl(callId: UUID): string | undefined;
  savedViews(): Promise<SavedView[]>;
  saveView(v: { name: string; query: Record<string, string>; is_shared: boolean }): Promise<SavedView>;
  deleteView(id: UUID): Promise<void>;
  create(input: NewJobInput, key: string): Promise<Job>;
  update(id: UUID, patch: JobPatch, o: Mut): Promise<Job>;
  action(id: UUID, action: string, body: ActionBody, o: Mut): Promise<Job>;
  saveEstimate(id: UUID, lines: LineInput[], o: Mut): Promise<Job>;
  sendEstimate(id: UUID, channel: 'sms' | 'email', o: Mut): Promise<Job>;
  approveOnBehalf(id: UUID, b: { approver_name: string; note: string }, o: Mut): Promise<Job>;
  recordCash(id: UUID, b: { amount_cents: Cents; method: 'cash' | 'check'; note: string | null }, o: Mut): Promise<unknown>;
}

const enc = encodeURIComponent;
type Row = Record<string, any>;
const items = <T>(r: { items: T[] } | T[]) => (Array.isArray(r) ? r : r.items);
/** Drop null/undefined/'' so P1's zod `.optional()` fields validate. */
const clean = <T extends Record<string, unknown>>(o: T) => Object.fromEntries(Object.entries(o).filter(([, v]) => v != null && v !== '')) as Partial<T>;
const lineBody = (l: LineInput) => clean({ kind: l.kind, description: l.description, qty: l.qty, unit_price_cents: l.unit_price_cents, taxable: l.taxable, catalog_item_id: l.catalog_item_id });
const minutes = (a: ISO | null, b: ISO | null) => (a && b ? Math.max(1, Math.round((Date.parse(b) - Date.parse(a)) / 60_000)) : undefined);

/**
 * Live = P1 api/src/modules/jobs (routes.js): `{job}` / `{items,next}` rows mapped by core/live/dto.ts; status changes
 * go through POST /jobs/:id/status {to, reason}; lines under /jobs/:id/lines; estimates /jobs/:id/estimates +
 * /estimates/:id/{send,approve,decline}. UI actions P1 has no route for throw `not_available` (see report).
 */
export const live = (http: Http): Api => {
  const names = new Map<string, string>();
  const fresh = (id: UUID) => fetchJob(http, id);
  const status = (id: UUID, to: string, o: Mut, extra: { reason?: string; override?: boolean } = {}) =>
    http.post(`${APP}/jobs/${enc(id)}/status`, clean({ to, ...extra }), { idempotencyKey: o.key }).then(() => fresh(id));
  /** The version the user saw vs the server's: P1 checks If-Match on PATCH only, so other writes are checked here. */
  const expect = async (id: UUID, o: Mut) => {
    const cur = await fresh(id);
    if (o.version != null && cur.version !== o.version) throw new ApiError(409, 'stale_version', null, { current_version: cur.version, changed_at: cur.status_changed_at });
    return cur;
  };
  const openEstimate = async (id: UUID, want: 'draft' | 'sent') => {
    const r = await http.get<{ job: Row }>(`${APP}/jobs/${enc(id)}`);
    const est = currentEstimate(r.job.estimates);
    if (!est || (want === 'sent' ? est.status !== 'sent' : !['draft', 'sent'].includes(est.status))) throw new ApiError(409, 'estimate_not_open', null);
    return est.id as string;
  };
  /** P1's PATCH 409 carries `{version}`; the UI's banner reads `current_version`. */
  const patchJob = (id: UUID, body: Row, o: Mut) => http.patch(`${APP}/jobs/${enc(id)}`, body, { version: o.version, idempotencyKey: o.key }).then(() => fresh(id), (e) => {
    if (e instanceof ApiError && e.code === 'stale_version') throw new ApiError(409, 'stale_version', e.requestId, { current_version: (e.body as Row | null)?.version ?? null });
    throw e;
  });
  const myName = () => http.get<Row>(`${APP}/me`).then((m) => [m.user?.first_name, m.user?.last_name].filter(Boolean).join(' ') || 'Office');

  return {
    events: (id) => http.get<{ items: Row[] }>(`${APP}/jobs/${enc(id)}/timeline`).then((r) => items(r).map((x) => apiTimeline(x, id, names))),
    // P4 lists calls without a job filter; narrow here.
    calls: (id) => http.get<{ calls: Row[] }>(`${APP}/calls`, { query: { limit: 200 } }).then((r) => (r.calls ?? []).filter((c) => c.job_id === id).map((c) => apiCall(c))),
    // No staff conversations route yet (P4): the thread degrades to empty.
    messages: async () => [],
    lead: (id) => http.get<{ lead: Row }>(`${APP}/leads/${enc(id)}`).then((r) => apiLead(r.lead, names)),
    // P5 geo is not mounted yet: typed addresses still work, suggestions come back empty.
    geoAutocomplete: (q) => http.get<{ items: GeoSuggestion[] } | GeoSuggestion[]>(`${APP}/geo/autocomplete`, { query: { q } }).then(items)
      .catch((e) => { if (isMissingRoute(e)) return []; throw e; }),
    recordingUrl: (callId) => `${API_BASE}${APP}/calls/${enc(callId)}/recording`,
    savedViews: () => http.get<{ items: SavedView[] }>(`${APP}/saved_views`, { query: { module: 'jobs' } }).then(items),
    saveView: (v) => http.post<{ view: SavedView }>(`${APP}/saved_views`, { module: 'jobs', ...v }).then((r) => r.view),
    deleteView: (id) => http.del(`${APP}/saved_views/${enc(id)}`).then(() => undefined),

    create: async (input, key) => {
      const c = input.customer;
      const v = input.vehicle;
      const a = input.address;
      const body = clean({
        lead_id: input.lead_id, customer_id: input.customer_id, location_id: input.location_id,
        customer: c && !input.customer_id ? clean({ first_name: c.first_name, last_name: c.last_name, company: c.company, phone_e164: c.phone_e164, email: c.email, kind: c.company ? 'business' : 'person' }) : null,
        vehicle_id: input.vehicle_id, vehicle: v && !input.vehicle_id ? clean({ year: v.year, make: v.make, model: v.model, vin: v.vin }) : null,
        address_id: a?.address_id, address: a && !a.address_id ? clean({ line1: a.line1, line2: a.line2, city: a.city, state: a.state, zip: a.zip ?? '', lat: a.lat, lng: a.lng }) : null,
        scheduled_start: input.scheduled_start, est_minutes: minutes(input.scheduled_start, input.scheduled_end),
        service_type: input.lines.map((l) => l.description).filter(Boolean).slice(0, 3).join(', ').slice(0, 200), customer_notes: input.customer_notes,
      });
      // From a lead: P1's convert links the lead and books it in the same transaction.
      const path = input.lead_id ? `${APP}/leads/${enc(input.lead_id)}/convert` : `${APP}/jobs`;
      const r = await http.post<{ job: Row }>(path, body, { idempotencyKey: key });
      const id = r.job.id as UUID;
      // Lines are separate writes; an idempotent replay finds them already there and adds nothing.
      const have = await http.get<{ lines: Row[] }>(`${APP}/jobs/${enc(id)}/lines`);
      if (!have.lines?.length) for (const l of input.lines) await http.post(`${APP}/jobs/${enc(id)}/lines`, lineBody(l), { idempotencyKey: `${key}:l${input.lines.indexOf(l)}` });
      return fresh(id);
    },
    update: async (id, patch, o) => {
      const body: Row = {};
      if (patch.internal_notes !== undefined) body.internal_notes = patch.internal_notes ?? '';
      if (patch.address) {
        const cur = await fresh(id);
        const a = patch.address;
        const addr = a.address_id ? { id: a.address_id } : await http.post<{ address: Row }>(`${APP}/customers/${enc(cur.customer_id)}/addresses`,
          clean({ line1: a.line1, line2: a.line2, city: a.city, state: a.state, zip: a.zip ?? '', lat: a.lat, lng: a.lng }), { idempotencyKey: o.key }).then((x) => x.address);
        body.address_id = addr.id;
        if (a.lat != null && a.lng != null) Object.assign(body, { lat: a.lat, lng: a.lng });
      }
      return patchJob(id, body, o);
    },
    action: async (id, a, b, o) => {
      switch (a) {
        case 'cancel': await expect(id, o); return status(id, b.reason ?? 'CANCELLED - OTHER REASONS', o, { reason: b.note });
        case 'no_show': await expect(id, o); return status(id, 'CANCELLED - CUSTOMER DID NOT RESPOND', o, { reason: b.note ?? 'no_show' });
        case 'duplicate': await expect(id, o); return status(id, 'DUPLICATE', o, { reason: b.reason });
        case 'override_status': await expect(id, o); return status(id, b.status ?? '', o, { reason: b.reason, override: true });
        case 'unassign': await expect(id, o); await http.post(`${APP}/jobs/${enc(id)}/unassign`, clean({ reason: b.reason }), { idempotencyKey: o.key }); return fresh(id);
        case 'reschedule':
          return patchJob(id, clean({ scheduled_start: b.start, est_minutes: minutes(b.start ?? null, b.end ?? null) }), o);
        case 'approve': {
          await expect(id, o);
          const eid = await openEstimate(id, 'sent');
          await http.post(`${APP}/estimates/${enc(eid)}/approve`, { name: await myName(), via: 'staff' }, { idempotencyKey: o.key });
          return fresh(id);
        }
        default: return notAvailable(`job_action:${a}`);
      }
    },
    saveEstimate: async (id, lines, o) => {
      await expect(id, o);
      await http.post(`${APP}/jobs/${enc(id)}/estimates`, { lines: lines.map(lineBody) }, { idempotencyKey: o.key });
      return fresh(id);
    },
    sendEstimate: async (id, channel, o) => {
      if (channel === 'email') return notAvailable('estimate_email');
      await expect(id, o);
      const eid = await openEstimate(id, 'draft');
      await http.post(`${APP}/estimates/${enc(eid)}/send`, {}, { idempotencyKey: o.key });
      return fresh(id);
    },
    approveOnBehalf: async (id, b, o) => {
      await expect(id, o);
      const eid = await openEstimate(id, 'sent');
      await http.post(`${APP}/estimates/${enc(eid)}/approve`, { name: b.approver_name, via: 'voice' }, { idempotencyKey: o.key });
      return fresh(id);
    },
    recordCash: (id, b, o) => http.post(`${APP}/pay/jobs/${enc(id)}/cash`, clean({ amount_cents: b.amount_cents, method: b.method, collected_by: 'office', note: b.note }), { idempotencyKey: o.key }),
  };
};
